/**
 * The one inspector frame (`?i=kind:id`, optimal decision 5): header with back/close, the body a
 * kind supplies, and the registry footer (`ActionFooter`) for the current selection. The Lanes
 * inspector and the Home/Inbox inspector both render through it, so a run, task, decision,
 * landing, build or bark looks and acts alike. A right pane on desktop, a full-screen push on
 * phones, never a bottom sheet.
 */
import { ArrowLeft, X } from 'lucide-react'
import { type ReactNode, type Ref, type TouchEvent, useRef } from 'react'
import type { ActionEntity } from '../../lib/actions.js'
import { useEntityActions, type VerbHandlers } from '../../lib/entity-actions.js'
import { useActionKeys, useSelection } from '../../lib/selection-actions.js'
import { useWide } from '../useWide.js'
import { ActionFooter } from './ActionFooter.js'

export interface InspectorFrameProps {
  /** `lk` (Lanes inspector) or `tr` (Home/Inbox inspector): the existing skin. */
  skin: 'lk' | 'tr'
  kind: string
  title: ReactNode
  sub?: ReactNode
  onClose: () => void
  backLabel?: string
  headingRef?: Ref<HTMLHeadingElement>
  /** Non-decision kinds: the frame publishes this entity's actions. A decision publishes its own. */
  entity?: ActionEntity | undefined
  projectId?: string | undefined
  handlers?: VerbHandlers
  /** Below the desktop breakpoint the pane is a full-screen dialog (the Lanes inspector). */
  dialog?: boolean
  /** app-clarity: a short kind label shown instead of the title ("Decision"); the title stays the
   * heading's accessible name. */
  kicker?: string
  /** Where this item sits in its list ("1 of 11") and the next one's title; j/k (and a swipe on
   * phones) step through it. */
  position?: { at: number; total: number; next?: string | undefined; label?: string }
  onStep?: (dir: 1 | -1) => void
  /** Quiet links in the head (desktop), e.g. "Open in Viko". */
  extra?: ReactNode
  children: ReactNode
}

export function InspectorFrame(p: InspectorFrameProps) {
  const wide = useWide()
  const list = useEntityActions(`frame:${p.kind}`, p.entity, {
    ...(p.projectId ? { projectId: p.projectId } : {}),
    ...(p.handlers ? { handlers: p.handlers } : {}),
  })
  // core-loop: the footer's keycaps are live keys (a decision binds its own).
  useActionKeys(list, { enabled: !!p.entity })
  const sel = useSelection()
  const s = p.skin
  // Phone: a horizontal swipe steps to the next (←) or previous (→) item.
  const start = useRef<{ x: number; y: number } | undefined>(undefined)
  const swipe = {
    onTouchStart: (e: TouchEvent) => {
      const t = e.touches[0]
      start.current = t ? { x: t.clientX, y: t.clientY } : undefined
    },
    onTouchEnd: (e: TouchEvent) => {
      const a = start.current
      const t = e.changedTouches[0]
      start.current = undefined
      if (!a || !t) return
      const dx = t.clientX - a.x
      const dy = t.clientY - a.y
      if (Math.abs(dx) > 64 && Math.abs(dx) > 2 * Math.abs(dy)) p.onStep?.(dx < 0 ? 1 : -1)
    },
  }
  const id = `${s}-insp-h`
  return (
    <aside
      className={s === 'lk' ? 'lk-insp' : 'tr-detail'}
      aria-labelledby={id}
      data-inspector={p.kind}
      {...(wide
        ? { role: 'complementary' }
        : p.dialog
          ? { role: 'dialog', 'aria-modal': true as const }
          : {})}
    >
      <div
        className={s === 'lk' ? 'lk-insp__head' : `tr-detail__head${p.kicker ? ' is-kicker' : ''}`}
      >
        <button
          type="button"
          className="vk-iconbtn"
          aria-label={wide ? 'Close' : (p.backLabel ?? 'Back')}
          onClick={p.onClose}
          {...(wide && p.kicker ? { hidden: true } : {})}
        >
          {wide ? <X size={18} aria-hidden="true" /> : <ArrowLeft size={18} aria-hidden="true" />}
        </button>
        <h2 id={id} ref={p.headingRef} tabIndex={-1}>
          {p.kicker ? (
            <>
              <span aria-hidden="true">{wide ? p.kicker : (p.position?.label ?? p.kicker)}</span>
              <span className="sr-only">{p.title}</span>
              {p.position ? (
                <span className="tr-detail__pos">
                  {p.position.at} of {p.position.total}
                </span>
              ) : null}
            </>
          ) : (
            p.title
          )}
        </h2>
        {s === 'lk' ? <span /> : null}
        {p.sub ? <p className="lk-insp__sub">{p.sub}</p> : null}
        {p.kicker && wide ? (
          <span className="tr-detail__tools">
            {p.extra}
            {p.onStep ? (
              <>
                <button
                  type="button"
                  className="tr-detail__step"
                  aria-label="Previous"
                  onClick={() => p.onStep?.(-1)}
                >
                  <kbd className="ak">j</kbd>
                </button>
                <button
                  type="button"
                  className="tr-detail__step"
                  aria-label="Next"
                  onClick={() => p.onStep?.(1)}
                >
                  <kbd className="ak">k</kbd>
                </button>
              </>
            ) : null}
            <button type="button" className="vk-iconbtn" aria-label="Close" onClick={p.onClose}>
              <X size={18} aria-hidden="true" />
            </button>
          </span>
        ) : null}
        {p.kicker && !wide && p.position?.next ? (
          <button type="button" className="tr-detail__next" onClick={() => p.onStep?.(1)}>
            <span className="tr-detail__nextlabel">Next: {p.position.next}</span>
            <span aria-hidden="true" className="tr-detail__swipe">
              ← swipe
            </span>
          </button>
        ) : null}
      </div>
      <div
        className={s === 'lk' ? 'lk-insp__body' : 'tr-detail__body'}
        {...(!wide && p.onStep ? swipe : {})}
      >
        {p.children}
      </div>
      {sel ? <ActionFooter actions={sel.actions} title={sel.title} /> : null}
    </aside>
  )
}
